import { PROTOCOL_VERSION, type ClientMessage, type ServerMessage } from './types';

const SERVER_TYPES = new Set<ServerMessage['type']>([
  'welcome',
  'snapshot',
  'update',
  'series.append',
  'event',
  'command',
  'ack',
  'pong',
  'error',
]);

/**
 * Validazione leggera dei messaggi in ingresso: controlla busta e tipo.
 * Il payload è considerato affidabile (viene dal nostro server); se in futuro
 * serve validazione completa, sostituire con uno schema zod generato dai tipi.
 */
export function parseServerMessage(raw: unknown): ServerMessage | null {
  if (typeof raw !== 'string') return null;
  let msg: unknown;
  try {
    msg = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!msg || typeof msg !== 'object') return null;
  const m = msg as Record<string, unknown>;
  if (m.v !== PROTOCOL_VERSION) return null;
  if (typeof m.type !== 'string' || !SERVER_TYPES.has(m.type as ServerMessage['type'])) return null;
  if (typeof m.ts !== 'number' || typeof m.payload !== 'object' || m.payload === null) return null;
  return msg as ServerMessage;
}

let counter = 0;
export function newId(prefix = 'c'): string {
  const rnd =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `${prefix}-${++counter}-${rnd}`;
}

/** Costruisce un messaggio client con busta completa. */
export function makeClientMessage<M extends ClientMessage>(
  type: M['type'],
  payload: M['payload'],
  withId = false,
): M {
  return {
    v: PROTOCOL_VERSION,
    type,
    ts: Date.now(),
    ...(withId ? { id: newId() } : {}),
    payload,
  } as M;
}
